import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Clock, User, ArrowRight, Heart, Play,
  Briefcase, MapPin, Headphones, CheckCircle, XCircle,
  TrendingUp, Newspaper, Loader2
} from "lucide-react";
import BreakingNewsTicker from "@/components/BreakingNewsTicker";
import Sidebar from "@/components/Sidebar";
import AudioPlayer from "@/components/AudioPlayer";
import SEOHead from "@/components/SEOHead";
import api from "@/lib/api";

export default function HomePage() {
  const [articles, setArticles] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [categories, setCategories] = useState([]);
  const [mythFacts, setMythFacts] = useState([]);
  const [business, setBusiness] = useState([]);
  const [podcasts, setPodcasts] = useState([]);
  const [videos, setVideos] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [playingEp, setPlayingEp] = useState(null);
  const [podEpisodes, setPodEpisodes] = useState({});
  const [newsTotal, setNewsTotal] = useState(0);
  const [loadingMore, setLoadingMore] = useState(false);
  const [newsPage, setNewsPage] = useState(1);

  useEffect(() => {
    const load = async () => {
      try {
        const [artRes, docRes, catRes, mfRes, bzRes, podRes, vidRes, jobRes] = await Promise.all([
          api.get("/articles?limit=8"),
          api.get("/doctors"),
          api.get("/categories"),
          api.get("/articles?is_myth_fact=true&limit=4"),
          api.get("/articles?category_slug=business-tech&limit=3"),
          api.get("/podcasts"),
          api.get("/videos"),
          api.get("/jobs"),
        ]);
        setArticles(artRes.data.articles || []);
        setNewsTotal(artRes.data.total || 0);
        setDoctors(docRes.data || []);
        setCategories(catRes.data || []);
        setMythFacts(mfRes.data.articles || []);
        setBusiness(bzRes.data.articles || []);
        setPodcasts(podRes.data || []);
        setVideos(vidRes.data || []);
        setJobs(jobRes.data || []);
      } catch (e) { console.error(e); }
      setLoading(false);
    };
    load();
  }, []);

  const loadEpisodes = async (podId) => {
    if (podEpisodes[podId]) return;
    try {
      const r = await api.get(`/episodes?podcast_id=${podId}`);
      setPodEpisodes(prev => ({ ...prev, [podId]: r.data }));
    } catch {}
  };

  const loadMoreNews = async () => {
    const skip = newsPage * 8;
    setLoadingMore(true);
    try {
      const r = await api.get(`/articles?limit=8&skip=${skip}`);
      const newArticles = r.data.articles || [];
      if (newArticles.length > 0) {
        setArticles(prev => [...prev, ...newArticles]);
        setNewsPage(prev => prev + 1);
      }
      setNewsTotal(r.data.total || newsTotal);
    } catch (e) { console.error(e); }
    setLoadingMore(false);
  };

  const featured = articles.find(a => a.is_featured) || articles[0];
  const latestNews = articles.filter(a => a.id !== featured?.id);
  const hasMoreNews = articles.length < newsTotal;
  const catMap = {};
  categories.forEach(c => { catMap[c.id] = c; });

  if (loading) {
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Skeleton className="h-[500px] w-full mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-64" />)}
        </div>
      </div>
    );
  }

  return (
    <main data-testid="home-page">
      <SEOHead title="Home" description="Trusted Medical News & Awareness Platform - Evidence-based healthcare content." type="website" />
      <BreakingNewsTicker />

      {/* Hero Section */}
      {featured && (
        <section className="relative overflow-hidden bg-slate-900" data-testid="hero-section">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[500px]">
              {/* Main hero */}
              <div className="lg:col-span-8 relative group">
                <img
                  src={featured.featured_image}
                  alt={featured.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  loading="lazy"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 via-slate-900/40 to-transparent" />
                <div className="relative h-full flex flex-col justify-end p-8 lg:p-12">
                  <Badge className="w-fit mb-4 bg-[#1E88E5] text-white border-none text-xs font-sans uppercase tracking-wider">
                    {catMap[featured.category_id]?.name || "Featured"}
                  </Badge>
                  <Link to={`/article/${featured.id}`} data-testid="hero-article-link">
                    <h1 className="text-3xl sm:text-4xl lg:text-5xl font-serif text-white leading-tight mb-4 hover:text-blue-200 transition-colors">
                      {featured.title}
                    </h1>
                  </Link>
                  <p className="text-slate-300 font-sans text-sm max-w-2xl mb-4 line-clamp-2">{featured.summary}</p>
                  <div className="flex items-center gap-4 text-slate-400 text-xs font-sans">
                    <span className="flex items-center gap-1"><User className="h-3 w-3" />{featured.author_name}</span>
                    <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{featured.reading_time}</span>
                  </div>
                </div>
              </div>
              {/* Side articles */}
              <div className="lg:col-span-4 bg-slate-900 flex flex-col divide-y divide-slate-800">
                {latestNews.slice(0, 3).map((art) => (
                  <Link
                    key={art.id}
                    to={`/article/${art.id}`}
                    className="flex-1 p-6 hover:bg-slate-800/50 transition-colors group"
                    data-testid={`hero-side-article-${art.id}`}
                  >
                    <Badge variant="outline" className="text-[10px] border-slate-600 text-slate-400 mb-2 font-sans">
                      {catMap[art.category_id]?.name || "News"}
                    </Badge>
                    <h3 className="font-serif text-white text-lg leading-snug group-hover:text-blue-300 transition-colors line-clamp-2">
                      {art.title}
                    </h3>
                    <span className="text-xs text-slate-500 font-sans mt-2 block">{art.reading_time}</span>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Latest Medical News */}
      <section className="py-16 lg:py-24" data-testid="latest-news-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
            <div className="lg:col-span-8">
              <SectionHeader icon={<Newspaper className="h-4 w-4" />} title="Latest Medical News" />
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {latestNews.map((art, i) => (
                  <ArticleCard key={art.id} article={art} category={catMap[art.category_id]} idx={i} />
                ))}
              </div>
              <div className="mt-8 text-center flex items-center justify-center gap-4">
                {hasMoreNews && (
                  <Button
                    variant="outline"
                    className="font-sans text-sm"
                    onClick={loadMoreNews}
                    disabled={loadingMore}
                    data-testid="load-more-news"
                  >
                    {loadingMore ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Loading...</> : <>Load More Articles</>}
                  </Button>
                )}
                <Link to="/category/medical-news">
                  <Button variant="outline" className="font-sans text-sm" data-testid="view-all-news">
                    View All News <ArrowRight className="h-4 w-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
            <div className="hidden lg:block lg:col-span-4">
              <Sidebar />
            </div>
          </div>
        </div>
      </section>

      {/* Doctor Speak */}
      {doctors.length > 0 && (
        <section className="py-16 border-y border-slate-200 dark:border-slate-800" data-testid="doctor-speak-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader icon={<CheckCircle className="h-4 w-4" />} title="Doctor Speak" subtitle="Expert opinions from verified medical professionals" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {doctors.map((doc) => (
                <div key={doc.id} className="border border-slate-200 dark:border-slate-700 p-8 flex flex-col items-center text-center hover:-translate-y-1 transition-transform bg-white dark:bg-slate-800" data-testid={`doctor-card-${doc.id}`}>
                  <img src={doc.profile_image} alt={doc.name} className="w-24 h-24 rounded-full object-cover mb-4 border-2 border-slate-200 dark:border-slate-600" loading="lazy" />
                  <h3 className="font-serif font-bold text-foreground text-lg">{doc.name}</h3>
                  <p className="text-sm font-sans text-[#1E88E5] font-medium mt-1">{doc.specialization}</p>
                  {doc.is_verified && (
                    <Badge className="mt-2 bg-[#26A69A] text-white border-none text-[10px] font-sans">
                      <CheckCircle className="h-3 w-3 mr-1" /> Verified
                    </Badge>
                  )}
                  <p className="text-xs font-sans text-muted-foreground mt-3 line-clamp-3">{doc.bio}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Health Awareness - Myth vs Fact */}
      {mythFacts.length > 0 && (
        <section className="py-16 bg-slate-50 dark:bg-slate-900/50 border-y border-slate-200 dark:border-slate-800" data-testid="myth-fact-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader icon={<Heart className="h-4 w-4" />} title="Health Awareness" subtitle="Myth vs Fact" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {mythFacts.map((mf) => (
                <div key={mf.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-8 relative overflow-hidden" data-testid={`myth-fact-${mf.id}`}>
                  <Link to={`/article/${mf.id}`} className="font-serif font-bold text-lg text-foreground hover:text-[#1E88E5] transition-colors block mb-6">
                    {mf.title}
                  </Link>
                  <div className="space-y-4">
                    <div className="flex gap-3">
                      <div className="shrink-0 w-8 h-8 bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <XCircle className="h-4 w-4 text-red-600" />
                      </div>
                      <div>
                        <span className="text-[10px] font-sans font-bold uppercase tracking-wider text-red-600">Myth</span>
                        <p className="text-sm font-sans text-muted-foreground mt-1">{mf.myth_text}</p>
                      </div>
                    </div>
                    <div className="flex gap-3">
                      <div className="shrink-0 w-8 h-8 bg-green-100 dark:bg-green-900/30 flex items-center justify-center">
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      </div>
                      <div>
                        <span className="text-[10px] font-sans font-bold uppercase tracking-wider text-green-600">Fact</span>
                        <p className="text-sm font-sans text-muted-foreground mt-1">{mf.fact_text}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Medical Business & Tech */}
      {business.length > 0 && (
        <section className="py-16 border-y border-slate-200 dark:border-slate-800" data-testid="business-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader icon={<TrendingUp className="h-4 w-4" />} title="Medical Business & Tech" />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {business.map((art) => (
                <ArticleCard key={art.id} article={art} category={catMap[art.category_id]} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Podcasts */}
      {podcasts.length > 0 && (
        <section className="py-16 lg:py-24" data-testid="podcasts-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader icon={<Headphones className="h-4 w-4" />} title="Podcasts" subtitle="Listen to expert medical discussions" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {podcasts.map((pod) => (
                <div key={pod.id} className="border border-slate-200 dark:border-slate-700 overflow-hidden" data-testid={`podcast-card-${pod.id}`}>
                  <div className="flex gap-4 p-6">
                    <img src={pod.cover_image} alt={pod.title} className="w-24 h-24 object-cover shrink-0" loading="lazy" />
                    <div className="min-w-0">
                      <Link to={`/podcast/${pod.id}`} className="font-serif font-bold text-xl text-foreground hover:text-[#1E88E5] transition-colors">{pod.title}</Link>
                      <p className="text-sm font-sans text-[#1E88E5] mt-1">Hosted by {pod.host_name}</p>
                      <p className="text-xs font-sans text-muted-foreground mt-2 line-clamp-2">{pod.description}</p>
                    </div>
                  </div>
                  <div className="border-t border-slate-200 dark:border-slate-700">
                    <button
                      onClick={() => loadEpisodes(pod.id)}
                      className="w-full px-6 py-3 text-sm font-sans text-[#1E88E5] hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors text-left"
                      data-testid={`load-episodes-${pod.id}`}
                    >
                      View Episodes <ArrowRight className="h-3 w-3 inline ml-1" />
                    </button>
                    {podEpisodes[pod.id] && (
                      <div className="border-t border-slate-200 dark:border-slate-700 divide-y divide-slate-100 dark:divide-slate-700">
                        {podEpisodes[pod.id].map((ep) => (
                          <div key={ep.id} className="px-6 py-3">
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-sm font-sans font-medium text-foreground">Ep {ep.episode_number}: {ep.title}</span>
                              <span className="text-xs font-sans text-muted-foreground">{ep.duration}</span>
                            </div>
                            {playingEp === ep.id ? (
                              <AudioPlayer src={ep.audio_url} title={ep.title} onEnded={() => setPlayingEp(null)} />
                            ) : (
                              <button
                                onClick={() => setPlayingEp(ep.id)}
                                className="flex items-center gap-2 text-xs font-sans text-[#1E88E5] hover:underline mt-1"
                                data-testid={`play-episode-${ep.id}`}
                              >
                                <Play className="h-3 w-3" /> Play Episode
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Videos */}
      {videos.length > 0 && (
        <section className="py-16 bg-slate-50 dark:bg-slate-900/50 border-y border-slate-200 dark:border-slate-800" data-testid="videos-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader icon={<Play className="h-4 w-4" />} title="Videos" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {videos.map((vid) => (
                <a key={vid.id} href={vid.video_url} target="_blank" rel="noopener noreferrer" className="group cursor-pointer" data-testid={`video-card-${vid.id}`}>
                  <div className="relative overflow-hidden border border-slate-200 dark:border-slate-700 aspect-video">
                    <img src={vid.thumbnail_url} alt={vid.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                    <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <div className="w-12 h-12 bg-white/90 flex items-center justify-center rounded-full">
                        <Play className="h-5 w-5 text-slate-900 ml-0.5" />
                      </div>
                    </div>
                    <span className="absolute bottom-2 right-2 bg-black/70 text-white text-[10px] font-sans px-2 py-0.5">{vid.duration}</span>
                  </div>
                  <h3 className="font-sans font-medium text-sm text-foreground mt-3 group-hover:text-[#1E88E5] transition-colors line-clamp-2">{vid.title}</h3>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Medical Jobs */}
      {jobs.length > 0 && (
        <section className="py-16 lg:py-24" data-testid="jobs-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader icon={<Briefcase className="h-4 w-4" />} title="Medical Careers" subtitle="Find your next opportunity in healthcare" />
            <div className="border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 divide-y divide-slate-100 dark:divide-slate-700">
              {jobs.map((job) => (
                <div key={job.id} className="p-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4 group hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors" data-testid={`job-card-${job.id}`}>
                  <div>
                    <h3 className="font-serif font-bold text-lg text-foreground">{job.title}</h3>
                    <div className="flex flex-wrap items-center gap-3 mt-2 text-sm font-sans text-muted-foreground">
                      <span>{job.company}</span>
                      <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{job.location}</span>
                      <Badge variant="outline" className="text-[10px] font-sans">{job.role_type}</Badge>
                      <span>{job.experience}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="font-sans font-semibold text-sm text-[#1E88E5]">{job.salary_range}</p>
                    <Button variant="outline" size="sm" className="mt-2 text-xs font-sans" data-testid={`apply-job-${job.id}`}>
                      Apply <ArrowRight className="h-3 w-3 ml-1" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Mobile Sidebar */}
      <section className="lg:hidden py-16 border-t border-slate-200 dark:border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <Sidebar />
        </div>
      </section>
    </main>
  );
}

function SectionHeader({ icon, title, subtitle, titleColor = "text-foreground" }) {
  return (
    <div className="mb-8">
      <div className="flex items-center gap-2 mb-2">
        <span className="text-[#1E88E5]">{icon}</span>
        <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">Section</span>
      </div>
      <h2 className={`text-3xl sm:text-4xl font-serif font-bold ${titleColor}`}>{title}</h2>
      {subtitle && <p className="text-sm font-sans text-muted-foreground mt-2">{subtitle}</p>}
    </div>
  );
}

function ArticleCard({ article, category, idx }) {
  return (
    <Link
      to={`/article/${article.id}`}
      className={`flex flex-col gap-4 group cursor-pointer animate-fade-in-up stagger-${(idx || 0) % 4 + 1}`}
      data-testid={`article-card-${article.id}`}
    >
      {article.featured_image && (
        <div className="aspect-[4/3] overflow-hidden border border-slate-200 dark:border-slate-700 relative">
          <img src={article.featured_image} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
        </div>
      )}
      <div>
        {category && <span className="text-[10px] font-sans font-bold uppercase tracking-[0.2em] text-[#1E88E5]">{category.name}</span>}
        <h3 className="text-xl font-serif text-foreground group-hover:text-[#1E88E5] transition-colors leading-snug mt-1 line-clamp-2">{article.title}</h3>
        <p className="text-sm font-sans text-muted-foreground mt-2 line-clamp-2">{article.summary}</p>
        <div className="flex items-center gap-3 mt-3 text-xs font-sans text-muted-foreground">
          {article.author_name && <span className="flex items-center gap-1"><User className="h-3 w-3" />{article.author_name}</span>}
          {article.reading_time && <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{article.reading_time}</span>}
        </div>
      </div>
    </Link>
  );
}
